<script setup lang="ts">
import ArticleCommentsForm from '@/pages/journalArticle/ui/ArticleCommentsForm.vue';
import ArticleCommentCard from '@/pages/journalArticle/ui/ArticleCommentCard.vue';
import useArticleComments from '@/pages/journalArticle/composables/useArticleComments';

const props = defineProps<{
  articleId?: number;
  articleAuthorId?: number;
  isLoadRequested?: boolean;
}>();

const isMobile: boolean = useIsMobile();

const commentText: Ref<string> = ref('');

const {
  commentsList,
  totalItems,
  isShowLoadMore,
  commentError,
  loadMore,
  addComment,
  resetCommentError,
  sendReply,
} = useArticleComments(
  computed(() => props.articleId),
  computed(() => Boolean(props.isLoadRequested)),
);

const createComment = async (text: string): Promise<void> => {
  const isSent = await addComment(text);
  if (isSent) commentText.value = '';
};
</script>

<template>
  <div :class="['article-comments', { _mobile: isMobile }]">
    <div class="article-comments__header">
      <div class="article-comments__title">
        Комментарии
        <span
          class="article-comments__count"
          itemprop="commentCount"
          data-testid="article-comments-count"
        >
          ({{ totalItems }})
        </span>
      </div>
    </div>
    <div class="article-comments__content-wrapper">
      <div class="article-comments__form-wrapper">
        <ArticleCommentsForm
          v-model="commentText"
          :error="commentError"
          @create="createComment"
          @reset-error="resetCommentError"
        />
      </div>
      <div v-if="commentsList.length" class="article-comments__content">
        <ul class="article-comments__list">
          <li
            v-for="comment in commentsList"
            :key="comment.id"
            class="article-comments__list-item"
            data-testid="article-comments-list-item"
          >
            <ArticleCommentCard
              :comment="comment"
              :article-author-id="articleAuthorId"
              @send="(text) => sendReply({ commentId: comment.id, text })"
            />
          </li>
        </ul>
        <button
          v-if="isShowLoadMore"
          class="article-comments__button"
          type="button"
          aria-label="Показать ещё комментарии"
          @click.prevent="loadMore"
        >
          Показать ещё
          <AppIcon class="article-comments__icon" name="ArrowDownSmall" />
        </button>
      </div>
      <div v-else class="article-comments__empty" data-testid="article-comments-empty">
        <AppIcon class="article-comments__empty-icon" name="Comment" />
        <div class="article-comments__empty-text">Комментариев пока нет</div>
      </div>
    </div>
  </div>
</template>

<style lang="less">
.article-comments {
  .flex-column(@size-x5);

  max-width: 720px;

  &__header {
    display: flex;
    justify-content: space-between;
  }

  &__title {
    display: flex;
    align-items: center;
    gap: @size-x3;
    font-size: @size-x6;
    line-height: @size-x6;
    font-weight: 700;
  }

  &__count {
    font-size: @size-x4;
    line-height: 21px;
    font-weight: 400;
    color: @color-silver-chalice;
  }

  &__button {
    .button-reset();

    display: inline-flex;
    align-items: center;
    font-size: 14px;
    line-height: 18px;
    font-weight: 600;

    &:hover {
      color: @color-persimmon;
    }
  }

  &__icon {
    svg path {
      stroke: @color-persimmon;
    }
  }

  &__content-wrapper {
    .flex-column(@size-x8);

    padding: @size-x8;
    border: 1px solid @color-gallery;
    border-radius: @main-size;
  }

  &__content {
    .flex-column(@size-x8);
  }

  &__list {
    .flex-column(@size-x8);
  }

  &__list-item {
    &:not(:last-child) {
      padding-bottom: @size-x8;
      border-bottom: 1px solid @color-gallery;
    }
  }

  &__empty {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    width: 100%;
    gap: 30px;
    min-height: 250px;
  }

  &__empty-text {
    font-size: @size-x6;
    font-weight: 600;
    line-height: 130%;
  }

  &._mobile {
    max-width: 100%;
  }

  &._mobile &__header {
    flex-wrap: wrap;
    gap: @size-x5;
  }

  &._mobile &__content-wrapper {
    padding: @size-x8 @size-x5;
  }

  &._mobile &__empty {
    gap: @size-x3;
    min-height: 155px;
  }

  &._mobile &__empty-icon svg {
    width: 80px;
    height: 80px;
  }

  &._mobile &__empty-text {
    font-size: 18px;
    line-height: 23px;
  }
}
</style>
